import { useEffect, useState } from "react";
import Modal from "@/components/Modal";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Supplier } from "@/redux/services/inventory/inventory.type";
import { useCrudSupplier } from "@/hooks/inventory/useCrudSupplier";
import {
  PAYMENT_TERMS,
  isPresetPaymentTerm,
} from "@/lib/paymentTerms";

const CUSTOM_SENTINEL = "__custom__";

interface SupplierFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  supplierToEdit?: Supplier;
  restaurantId: string;
  onCreated?: (supplier: Supplier) => void;
}

const SupplierFormModal = ({
  isOpen,
  onClose,
  supplierToEdit,
  restaurantId,
  onCreated,
}: SupplierFormModalProps) => {
  const isEditMode = !!supplierToEdit;
  const { register, errors, watch, setValue, onSubmit, isLoading } =
    useCrudSupplier(onClose, restaurantId, supplierToEdit, onCreated);

  const paymentTermsValue = watch("paymentTerms") ?? "";
  const [isCustomTerms, setIsCustomTerms] = useState(
    paymentTermsValue !== "" && !isPresetPaymentTerm(paymentTermsValue)
  );

  useEffect(() => {
    // Re-derive mode when the supplier being edited changes
    setIsCustomTerms(
      !!supplierToEdit?.paymentTerms &&
        !isPresetPaymentTerm(supplierToEdit.paymentTerms)
    );
  }, [supplierToEdit]);

  const selectValue = isCustomTerms
    ? CUSTOM_SENTINEL
    : paymentTermsValue || "";

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? "Modifier le fournisseur" : "Ajouter un fournisseur"}
      size="large"
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="supplier-name"
              className="block text-sm font-medium text-card-foreground mb-1"
            >
              Nom <span className="text-danger">*</span>
            </label>
            <input
              id="supplier-name"
              type="text"
              {...register("name")}
              className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Ex: Wholefoods Co"
            />
            {errors.name && (
              <p className="mt-1 text-xs text-danger">
                {errors.name.message}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="supplier-contact"
              className="block text-sm font-medium text-card-foreground mb-1"
            >
              Contact
            </label>
            <input
              id="supplier-contact"
              type="text"
              {...register("contactName")}
              className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Nom du contact"
            />
          </div>

          <div>
            <label
              htmlFor="supplier-phone"
              className="block text-sm font-medium text-card-foreground mb-1"
            >
              Téléphone
            </label>
            <input
              id="supplier-phone"
              type="tel"
              {...register("phone")}
              className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="+224 ..."
            />
          </div>

          <div>
            <label
              htmlFor="supplier-email"
              className="block text-sm font-medium text-card-foreground mb-1"
            >
              Email
            </label>
            <input
              id="supplier-email"
              type="email"
              {...register("email")}
              className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="email@exemple.com"
            />
            {errors.email && (
              <p className="mt-1 text-xs text-danger">
                {errors.email.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-card-foreground mb-1">
              Conditions de paiement
            </label>
            <Select
              value={selectValue}
              onValueChange={(v) => {
                if (v === CUSTOM_SENTINEL) {
                  setIsCustomTerms(true);
                  setValue("paymentTerms", "", { shouldValidate: true });
                } else {
                  setIsCustomTerms(false);
                  setValue("paymentTerms", v, { shouldValidate: true });
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner..." />
              </SelectTrigger>
              <SelectContent>
                {PAYMENT_TERMS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
                <SelectItem value={CUSTOM_SENTINEL}>Autre…</SelectItem>
              </SelectContent>
            </Select>
            {isCustomTerms && (
              <input
                type="text"
                {...register("paymentTerms")}
                className="mt-2 w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Saisir les conditions personnalisées"
              />
            )}
          </div>

          <div>
            <label
              htmlFor="supplier-lead"
              className="block text-sm font-medium text-card-foreground mb-1"
            >
              Délai de livraison (jours)
            </label>
            <input
              id="supplier-lead"
              type="number"
              min={0}
              {...register("leadTimeDays")}
              className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Ex: 2"
            />
            {errors.leadTimeDays && (
              <p className="mt-1 text-xs text-danger">
                {errors.leadTimeDays.message}
              </p>
            )}
          </div>
        </div>

        <div>
          <label
            htmlFor="supplier-notes"
            className="block text-sm font-medium text-card-foreground mb-1"
          >
            Notes
          </label>
          <textarea
            id="supplier-notes"
            {...register("notes")}
            rows={3}
            className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            placeholder="Notes internes..."
          />
        </div>

        <div className="flex justify-end pt-2">
          <Button type="submit" isLoading={isLoading} size="sm">
            Enregistrer
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default SupplierFormModal;
